import type { ReservationItemSplitMode } from "./types";

// Connection info that will be passed down from parent component
export interface ConnectionInfo {
    showBefore: boolean;
    showAfter: boolean;
    beforeColor: 'green' | 'yellow' | 'gray' | null;  // gray = artificial connection (out of order split)
    afterColor: 'green' | 'yellow' | 'gray' | null;
    breakDaysBefore?: number;
    breakDaysAfter?: number;
}

/**
 * Gets the connection color based on split mode
 */
export function getConnectionColor(splitMode: ReservationItemSplitMode | null | undefined): 'green' | 'yellow' | null {
    switch (splitMode) {
        case 'Combined': return 'green';
        case 'Extended': return 'yellow';
        case 'Disconnected': return null;
        default: return null;
    }
}

/**
 * Calculates the number of break nights between two dates.
 * For rooms: checkout date to next checkin date
 * For programs: end date to next start date
 */
export function calculateBreakNights(endDate: Date, startDate: Date): number {
    if (!endDate || !startDate) return 0;
    
    const timeDiff = startDate.getTime() - endDate.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    
    // For rooms: if checkout is Jan 5 12:00 and next checkin is Jan 7 14:00
    // Break nights are Jan 5, Jan 6 = 2 nights
    // daysDiff = ceil((Jan 7 14:00 - Jan 5 12:00) in days) = ceil(2.08) = 3
    // breakNights = 3 - 1 = 2
    return Math.max(0, daysDiff - 1);
}

/**
 * Generic interface for items that can be part of a split chain
 */
export interface ChainableItem {
    id: string;
    splitFromId: string | null;
    splitMode: ReservationItemSplitMode | null;
    startDate: Date;
    endDate: Date;
}

/**
 * Finds all items in the same connected component (island) as the given item.
 * Returns items sorted by start date.
 */
export function getIslandItems<T extends ChainableItem>(startItemId: string, allItems: T[]): T[] {
    const visited = new Set<string>();
    const island: T[] = [];
    
    // Find the starting item
    const startItem = allItems.find(item => item.id === startItemId);
    if (!startItem) return [];
    
    // Build a map for quick lookup
    const itemMap = new Map<string, T>();
    allItems.forEach(item => itemMap.set(item.id, item));
    
    // Build reverse links (parent -> children)
    const childrenMap = new Map<string, T[]>();
    allItems.forEach(item => {
        if (item.splitFromId) {
            const children = childrenMap.get(item.splitFromId) || [];
            children.push(item);
            childrenMap.set(item.splitFromId, children);
        }
    });
    
    // Find the root by walking up the splitFrom chain
    let root = startItem;
    while (root.splitFromId && itemMap.has(root.splitFromId)) {
        root = itemMap.get(root.splitFromId)!;
    }
    
    // BFS/DFS from root to find all connected items
    const queue: T[] = [root];
    while (queue.length > 0) {
        const current = queue.shift()!;
        if (visited.has(current.id)) continue;
        
        visited.add(current.id);
        island.push(current);
        
        // Add children (items that split from current)
        const children = childrenMap.get(current.id) || [];
        children.forEach(child => {
            if (!visited.has(child.id)) {
                queue.push(child);
            }
        });
    }
    
    // Sort by start date
    return island.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
}

/**
 * Finds the closest common ancestor of two items in the island.
 * Returns the ancestor item and the split modes of the paths from the ancestor to each item.
 */
function findCommonAncestor<T extends ChainableItem>(
    itemA: T,
    itemB: T,
    itemMap: Map<string, T>
): { ancestor: T | null; pathASplitModes: (ReservationItemSplitMode | null)[]; pathBSplitModes: (ReservationItemSplitMode | null)[] } {
    // Build the path from itemA to root
    const pathA: T[] = [itemA];
    const pathASplitModes: (ReservationItemSplitMode | null)[] = [itemA.splitMode];
    let current = itemA;
    while (current.splitFromId && itemMap.has(current.splitFromId)) {
        current = itemMap.get(current.splitFromId)!;
        pathA.push(current);
        pathASplitModes.push(current.splitMode);
    }
    
    // Build the path from itemB to root
    const pathB: T[] = [itemB];
    const pathBSplitModes: (ReservationItemSplitMode | null)[] = [itemB.splitMode];
    current = itemB;
    while (current.splitFromId && itemMap.has(current.splitFromId)) {
        current = itemMap.get(current.splitFromId)!;
        pathB.push(current);
        pathBSplitModes.push(current.splitMode);
    }
    
    // Find the first common element (closest common ancestor)
    const pathASet = new Set(pathA.map(item => item.id));
    for (let i = 0; i < pathB.length; i++) {
        if (pathASet.has(pathB[i].id)) {
            // Found common ancestor
            const ancestorIdx = pathA.findIndex(item => item.id === pathB[i].id);
            return {
                ancestor: pathB[i],
                pathASplitModes: pathASplitModes.slice(0, ancestorIdx + 1),
                pathBSplitModes: pathBSplitModes.slice(0, i + 1)
            };
        }
    }
    
    return { ancestor: null, pathASplitModes: [], pathBSplitModes: [] };
}

/**
 * Determines the best color to use for an artificial connection.
 *
 * Logic:
 * 1. If the entire island has only one split mode, use that color
 * 2. If the two items share a common ancestor and all paths to that ancestor
 *    use the same split mode, use that color
 * 3. Otherwise, use gray
 */
function getArtificialConnectionColor<T extends ChainableItem>(
    itemA: T,
    itemB: T,
    island: T[],
    uniformColor: 'green' | 'yellow' | null,
    itemMap: Map<string, T>
): 'green' | 'yellow' | 'gray' {
    // If the island has a uniform color, use it
    if (uniformColor) {
        return uniformColor;
    }
    
    // Check if both items share a common ancestor with the same split mode
    const { ancestor, pathASplitModes, pathBSplitModes } = findCommonAncestor(itemA, itemB, itemMap);
    
    if (ancestor) {
        // Get unique split modes in both paths (excluding nulls and the ancestor itself)
        const allModes = [
            ...pathASplitModes.filter(m => m !== null && m !== 'Disconnected'),
            ...pathBSplitModes.filter(m => m !== null && m !== 'Disconnected')
        ] as ReservationItemSplitMode[];
        
        const uniqueModes = new Set(allModes);
        
        // If all split modes are the same, use that color
        if (uniqueModes.size === 1) {
            const mode = allModes[0];
            return getConnectionColor(mode) || 'gray';
        }
    }
    
    // Default to gray for mixed colors
    return 'gray';
}

/**
 * Calculates connection info for each item in an island.
 * Returns a map of item ID to connection info.
 */
export function calculateIslandConnections<T extends ChainableItem>(
    island: T[]
): Map<string, ConnectionInfo> {
    const result = new Map<string, ConnectionInfo>();
    
    // Build a map for quick lookup
    const itemMap = new Map<string, T>();
    island.forEach(item => itemMap.set(item.id, item));
    
    // Check if the entire island has a uniform split mode
    const allSplitModes = island
        .filter(item => item.splitFromId !== null)  // Only items that were split from something
        .map(item => item.splitMode)
        .filter(mode => mode !== null && mode !== 'Disconnected');
    
    let uniformColor: 'green' | 'yellow' | null = null;
    if (allSplitModes.length > 0) {
        const uniqueModes = new Set(allSplitModes);
        if (uniqueModes.size === 1) {
            uniformColor = getConnectionColor(allSplitModes[0]);
        }
    }
    
    for (let i = 0; i < island.length; i++) {
        const current = island[i];
        const prev = i > 0 ? island[i - 1] : null;
        const next = i < island.length - 1 ? island[i + 1] : null;
        
        // Determine if the connection to prev/next is "natural" (follows splitFrom links)
        // or "artificial" (chronologically adjacent but not directly linked)
        const isNaturalPrev = prev && (
            current.splitFromId === prev.id ||  // current splits from prev
            prev.splitFromId === current.id     // prev splits from current (unusual but possible)
        );
        
        const isNaturalNext = next && (
            next.splitFromId === current.id ||  // next splits from current
            current.splitFromId === next.id     // current splits from next (unusual but possible)
        );
        
        // Get colors
        let beforeColor: 'green' | 'yellow' | 'gray' | null = null;
        let afterColor: 'green' | 'yellow' | 'gray' | null = null;
        
        if (prev) {
            if (isNaturalPrev) {
                // Use the split mode of the item that was split (the one with the splitFromId)
                const splitMode = current.splitFromId === prev.id ? current.splitMode : prev.splitMode;
                beforeColor = getConnectionColor(splitMode) || 'gray';
            } else {
                // Artificial connection - use smart color detection
                beforeColor = getArtificialConnectionColor(prev, current, island, uniformColor, itemMap);
            }
        }
        
        if (next) {
            if (isNaturalNext) {
                // Use the split mode of the item that was split
                const splitMode = next.splitFromId === current.id ? next.splitMode : current.splitMode;
                afterColor = getConnectionColor(splitMode) || 'gray';
            } else {
                // Artificial connection - use smart color detection
                afterColor = getArtificialConnectionColor(current, next, island, uniformColor, itemMap);
            }
        }
        
        // Calculate break days
        const breakDaysBefore = prev ? calculateBreakNights(prev.endDate, current.startDate) : undefined;
        const breakDaysAfter = next ? calculateBreakNights(current.endDate, next.startDate) : undefined;
        
        // Determine if we should show connection lines
        // Don't show if the split mode is 'Disconnected' for natural connections
        // Always show for artificial connections (gray)
        const showBefore = prev !== null && beforeColor !== null;
        const showAfter = next !== null && afterColor !== null;
        
        result.set(current.id, {
            showBefore,
            showAfter,
            beforeColor,
            afterColor,
            breakDaysBefore,
            breakDaysAfter
        });
    }
    
    return result;
}

/**
 * Groups items into islands (connected components) and calculates all connection info.
 * Returns islands sorted by the minimum start date of each island.
 */
export interface Island<T> {
    items: T[];  // Items sorted by start date
    connections: Map<string, ConnectionInfo>;  // Connection info for each item
    minStartDate: Date;  // Used for sorting islands
}

export function buildIslands<T extends ChainableItem>(allItems: T[]): Island<T>[] {
    const visited = new Set<string>();
    const islands: Island<T>[] = [];
    
    // Sort all items by start date first
    const sortedItems = [...allItems].sort((a, b) => 
        a.startDate.getTime() - b.startDate.getTime()
    );
    
    for (const item of sortedItems) {
        if (visited.has(item.id)) continue;
        
        // Get all items in this island
        const islandItems = getIslandItems(item.id, allItems);
        
        // Mark all items in this island as visited
        islandItems.forEach(i => visited.add(i.id));
        
        if (islandItems.length === 0) continue;
        
        // Calculate connections for this island
        const connections = calculateIslandConnections(islandItems);
        
        islands.push({
            items: islandItems,
            connections,
            minStartDate: islandItems[0].startDate  // Already sorted, so first item has min date
        });
    }
    
    // Sort islands by their minimum start date
    return islands.sort((a, b) => a.minStartDate.getTime() - b.minStartDate.getTime());
}

// ── Chain management utilities ──────────────────────────────────────────────

/**
 * Checks whether linking childId → parentId would create a circular chain.
 * Walks up from parentId through splitFrom ancestors; if we encounter childId, it's circular.
 */
export function wouldCreateCircularChain<T extends ChainableItem>(
    childId: string,
    parentId: string,
    allItems: T[]
): boolean {
    if (childId === parentId) return true;

    const itemMap = new Map<string, T>();
    allItems.forEach(item => itemMap.set(item.id, item));

    let current = itemMap.get(parentId);
    while (current) {
        if (current.id === childId) return true;
        if (!current.splitFromId) break;
        current = itemMap.get(current.splitFromId);
    }
    return false;
}

/**
 * Returns the IDs of items that are direct children of the given item
 * (i.e. items whose splitFromId points to itemId).
 */
export function getDirectChildren<T extends ChainableItem>(
    itemId: string,
    allItems: T[]
): T[] {
    return allItems.filter(item => item.splitFromId === itemId);
}
